import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Instagram, Clock, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const TIME_SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM"];

const inputCls = "w-full bg-white/10 border border-white/25 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/50 focus:ring-2 focus:ring-[#C5A059]/60 focus:outline-none";

export const BookingSection = ({ services, staff }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_id: "", staff_id: "", date: "", time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_id || !form.date || !form.time) {
      toast.error("Please fill name, phone, service, date and time.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", { ...form, staff_id: form.staff_id || null });
      toast.success("Appointment requested! We'll confirm shortly on your phone.");
      setForm({ name: "", phone: "", email: "", service_id: "", staff_id: "", date: "", time: "", notes: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="py-24 lg:py-32 bg-[#0A3F2F]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-2">
          <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs font-semibold mb-4">Reserve Your Seat</p>
          <h2 className="font-serif-brand text-4xl sm:text-5xl text-[#FAFAF8] font-medium tracking-tight mb-6">Book an Appointment</h2>
          <p className="text-white/70 font-light leading-relaxed">
            Choose your service and preferred time — our team will confirm your booking within the hour.
          </p>
        </motion.div>
        <motion.form onSubmit={submit} data-testid="booking-form"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
          <input data-testid="booking-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
          <input data-testid="booking-phone-input" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
          <input data-testid="booking-email-input" className={`${inputCls} sm:col-span-2`} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
          <select data-testid="booking-service-select" className={inputCls} value={form.service_id} onChange={set("service_id")}>
            <option value="" className="text-black">Select service *</option>
            {services.map((s) => (
              <option key={s.id} value={s.id} className="text-black">{s.name} — ₹{s.price.toLocaleString("en-IN")}</option>
            ))}
          </select>
          <select data-testid="booking-staff-select" className={inputCls} value={form.staff_id} onChange={set("staff_id")}>
            <option value="" className="text-black">Any available stylist</option>
            {staff.map((m) => (
              <option key={m.id} value={m.id} className="text-black">{m.name} — {m.role}</option>
            ))}
          </select>
          <input data-testid="booking-date-input" type="date" className={inputCls} value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} />
          <select data-testid="booking-time-select" className={inputCls} value={form.time} onChange={set("time")}>
            <option value="" className="text-black">Select time *</option>
            {TIME_SLOTS.map((t) => <option key={t} value={t} className="text-black">{t}</option>)}
          </select>
          <textarea data-testid="booking-notes-input" className={`${inputCls} sm:col-span-2`} rows={3} placeholder="Notes (optional)" value={form.notes} onChange={set("notes")} />
          <button type="submit" disabled={submitting} data-testid="booking-submit-btn"
            className="btn-lift sm:col-span-2 bg-[#C5A059] text-[#1C1C1C] font-semibold py-3.5 rounded-full text-sm hover:bg-[#d4b06c] disabled:opacity-60">
            {submitting ? "Requesting…" : "Request Appointment"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export const LocationSection = () => (
  <section id="location" className="bg-[#FAFAF8] py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs font-semibold mb-3">Visit Us</p>
          <h2 className="font-serif-brand text-4xl sm:text-5xl text-[#1C1C1C] font-medium tracking-tight">Find Us in Sonipat</h2>
          <p className="text-[#5C6A63] font-light mt-4 max-w-md">
            Near R.K. Sweets, Jandi Mandir Rd, Ashok Nagar (Subhash Chowk), Sonipat, Haryana 131001
          </p>
        </div>
        <a href="https://www.google.com/maps/search/?api=1&query=GREEN+Professionals+Salon+%26+Academy+Sonipat" target="_blank" rel="noopener noreferrer"
          data-testid="get-directions-btn"
          className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-6 py-3 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
          <MapPin className="w-4 h-4" /> Get Directions
        </a>
      </div>
      <div className="rounded-md overflow-hidden border border-[#E5E7E5] shadow-[0_4px_20px_rgba(10,63,47,0.08)]">
        <iframe
          title="GREEN Professionals Salon & Academy location map"
          data-testid="location-map"
          src="https://www.google.com/maps?q=GREEN+Professionals+Salon+%26+Academy,+Ashok+Nagar,+Sonipat,+Haryana+131001&output=embed"
          className="w-full h-[420px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer id="contact" className="bg-[#062A20] text-white/80 py-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-12">
      <div>
        <p className="font-serif-brand text-3xl text-[#FAFAF8] font-semibold mb-2">Green</p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-6">Professional Salon &amp; Academy</p>
        <p className="text-sm font-light leading-relaxed max-w-xs">Premium hair transformation, chemical artistry &amp; HD bridal makeovers in Sonipat — with a certified cosmetology &amp; hair academy.</p>
      </div>
      <div className="space-y-4 text-sm" data-testid="contact-info">
        <a href="https://www.google.com/maps/search/?api=1&query=GREEN+Professionals+Salon+%26+Academy+Sonipat" target="_blank" rel="noopener noreferrer"
          data-testid="footer-maps-link" className="flex items-start gap-3 hover:text-white transition-colors underline decoration-[#C5A059]/50 underline-offset-4">
          <MapPin className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" /> Near R.K. Sweets, Jandi Mandir Rd, Ashok Nagar (Subhash Chowk), Sonipat, Haryana 131001 — Open in Google Maps
        </a>
        <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#C5A059]" /> +91 70567 49191</p>
        <a href="https://www.instagram.com/green_professional_salon/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link"
          className="flex items-center gap-3 hover:text-white transition-colors"><Instagram className="w-4 h-4 text-[#C5A059]" /> @green_professional_salon</a>
        <p className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#C5A059]" /> Open daily · 10:00 AM – 8:00 PM</p>
        <p className="flex items-center gap-3"><Star className="w-4 h-4 text-[#C5A059]" /> 4.3 ★ · 266+ happy client reviews</p>
      </div>
      <div className="text-sm space-y-3">
        <p className="text-[#C5A059] uppercase tracking-wider text-xs font-semibold mb-4">Quick Links</p>
        <a href="#services" className="block hover:text-white transition-colors">Salon Services</a>
        <a href="#packages" className="block hover:text-white transition-colors">Packages</a>
        <a href="#academy" className="block hover:text-white transition-colors">Academy Courses</a>
        <a href="#gallery" className="block hover:text-white transition-colors">Gallery</a>
        <a href="#about" className="block hover:text-white transition-colors">About Us</a>
        <a href="#book" className="block hover:text-white transition-colors">Book Appointment</a>
        <a href="#location" className="block hover:text-white transition-colors">Google Map Location</a>
        <a href="https://wa.me/917056749191" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
          className="block hover:text-white transition-colors">WhatsApp Us</a>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-16 pt-8 border-t border-white/10 text-xs text-white/50">
      © {new Date().getFullYear()} Green Professional Salon &amp; Academy. All rights reserved.
    </div>
  </footer>
);
